/**
 * Troubleshoot – Hulp bij problemen met de website en gebruik
 */
import { Link } from 'react-router-dom';
import { HelpCircle, MessageCircle, FileQuestion, Download, RefreshCw, ScanLine, Smartphone } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

const ISSUES = [
  {
    icon: Smartphone,
    title: 'Ik werd plotseling uitgelogd op een ander apparaat',
    steps: [
      'CalqTool beperkt het aantal gelijktijdige inlogs per gebruikersaccount volgens jullie pakket (Basis 1, Plus 3, Pro 5). Nieuwe login op een extra apparaat kan de oudste sessie beëindigen.',
      'Log op het andere apparaat opnieuw in met hetzelfde e-mailadres en wachtwoord.',
      'Meer uitleg: Account → Inloggen & apparaten, of de pagina Pakketten (/prijzen).',
    ],
  },
  {
    icon: ScanLine,
    title: 'Tekeninganalyse: te weinig posten of geen bibliotheek-koppeling',
    steps: [
      'Controleer klanttype en dossiergegevens — die sturen het importadvies (kern/aanbevolen); de scan zelf is voor alle types even uitgebreid.',
      'Gebruik duidelijke bestandsnamen of hernoem documenten in de bibliotheek (potlood), bijv. verdieping of bestaand/ontwerp.',
      'Maximaal tien platen per analyse: PDF-pagina’s tellen mee. Verminder bestanden of splits in twee analyses.',
      'Je kunt documenten uit de documentenbibliotheek aanvinken onder Tekening analyseren — geen dubbele upload nodig.',
      'Je hoeft niet alles in één keer te importeren: niet-gekozen posten blijven in de opgeslagen analyse voor een volgende ronde op Gegevens.',
    ],
  },
  {
    icon: MessageCircle,
    title: 'De assistent werkt niet',
    steps: [
      'Controleer of je verbinding met internet hebt.',
      'Ververs de pagina en probeer opnieuw.',
      'Als je veel berichten hebt verstuurd, wacht even – er geldt een limiet.',
      'Bekijk de dienststatus (/status) of er een storing is.',
    ],
  },
  {
    icon: FileQuestion,
    title: 'Project laadt niet of geeft een fout',
    steps: [
      'Ververs de pagina.',
      'Controleer of je het juiste project hebt geopend.',
      'Probeer in te loggen als je dat nog niet hebt gedaan.',
    ],
  },
  {
    icon: Download,
    title: 'Export of download werkt niet',
    steps: [
      'Zorg dat je op tab Rapportage bent en dat Posten zijn opgeslagen (anders geen aggregatie).',
      'Lees kort het verschil tussen calculatie-export en offerte op de handleiding Export & offerte (/help/export).',
      'Controleer of je browser downloads niet blokkeert.',
      'Probeer een andere browser (Chrome, Firefox, Edge).',
    ],
  },
  {
    icon: RefreshCw,
    title: 'Pagina laadt traag of niet',
    steps: [
      'Controleer je internetverbinding.',
      'Ververs de pagina (F5 of Ctrl+R).',
      'Wis de cache van je browser en probeer opnieuw.',
      'Bekijk de dienststatus voor actuele informatie.',
    ],
  },
];

export function Troubleshoot() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/troubleshoot"
        title="Problemen oplossen"
        description="Veelvoorkomende CalqTool™-problemen: inloggen, sessie, uploads en browser. Stappenplan en verwijzing naar dienststatus bij grotere storingen."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Problemen oplossen' }]} />
        <h1 className="mb-2 text-2xl font-bold text-text">Problemen oplossen</h1>
        <p className="mb-6 max-w-2xl text-text-muted">
          Kom je ergens niet uit? Hieronder staan concrete stappen. Werkt de site mogelijk niet door een storing? Check
          eerst de{' '}
          <Link to="/status" className="font-medium text-primary hover:underline">
            dienststatus
          </Link>
          .
        </p>

        <div className="mb-10 rounded-xl border border-primary/20 bg-primary/[0.06] p-5">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-muted">Handleidingen</h2>
          <p className="mb-3 text-sm text-text-muted">
            Diepgaande uitleg per onderwerp — ook zonder ingelogd te zijn (zelfde pagina&apos;s als onder Account → Hulp
            &amp; contact en in de footer).
          </p>
          <ul className="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6">
            <li>
              <Link to="/help/tekening" className="font-medium text-primary hover:underline">
                Tekening &amp; PDF →
              </Link>
            </li>
            <li>
              <Link to="/help/export" className="font-medium text-primary hover:underline">
                Export &amp; offerte →
              </Link>
            </li>
            <li>
              <Link to="/help/bibliotheek" className="font-medium text-primary hover:underline">
                Bibliotheek →
              </Link>
            </li>
            <li>
              <Link to="/onboarding" className="font-medium text-primary hover:underline">
                Introductie (ingelogd) →
              </Link>
            </li>
          </ul>
        </div>

        <div className="space-y-8">
          {ISSUES.map((issue, i) => (
            <AnimatedSection key={i} delay={0.03 * i}>
              <div className="rounded-lg border border-border bg-surface-card p-6">
                <h2 className="mb-4 flex items-center gap-2 font-semibold text-text">
                  <issue.icon className="h-5 w-5 text-primary" />
                  {issue.title}
                </h2>
                <ol className="list-inside list-decimal space-y-2 text-sm text-text-muted">
                  {issue.steps.map((step, j) => (
                    <li key={j}>{step}</li>
                  ))}
                </ol>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/status"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text transition hover:border-primary hover:text-primary"
          >
            Dienststatus
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-alt"
          >
            <HelpCircle className="h-4 w-4" />
            Neem contact op
          </Link>
        </div>
      </AnimatedSection>
    </div>
  );
}
